import { useEffect, useState } from 'react';

import { useBridgewayApi } from '@/api/BridgewayApi';
import { useUserId } from '@/auth/UserIdContext';
import { logger } from '@/logger';
import {
	CATEGORY_LABEL,
	CATEGORY_PILL,
} from '@/spaces/chat/insightCategoryLabels';
import { SeededJobPanel } from '@/spaces/chat/panels/SeededJobPanel';
import type { CuratedInsight, CuratedInsightCategory } from '@/types/inbox';
import { cn } from '@/utils/cn';
import { formatRelativeTime } from '@/utils/formatRelativeTime';

import type { PanelProcess } from '../../../../../bridge/types/state.ts';

interface Competitor {
	id: string;
	name: string;
	last_analyzed_at?: string;
	social_media?: {
		instagram?: { url?: string };
	};
}

interface CompetitorMonitorPanelProps {
	panelProcess: PanelProcess;
	onBack?: () => void;
	onClose: () => void;
	style?: React.CSSProperties;
}

export const CompetitorMonitorPanel = ({
	panelProcess,
	onBack,
	onClose,
	style,
}: CompetitorMonitorPanelProps) => {
	const userId = useUserId();
	const api = useBridgewayApi();
	const [competitors, setCompetitors] = useState<Competitor[]>([]);
	const [insights, setInsights] = useState<CuratedInsight[]>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		if (!userId) return;
		Promise.all([
			api
				.get<Competitor[]>(`/api/user/${userId}/competitors/`)
				.catch(() => [] as Competitor[]),
			api
				.get<CuratedInsight[]>(`/api/user/${userId}/curated_insights/`)
				.catch(() => [] as CuratedInsight[]),
		])
			.then(([comps, curated]) => {
				setCompetitors(Array.isArray(comps) ? comps : []);
				const recent = (Array.isArray(curated) ? curated : [])
					.filter((c) => c.status === 'new')
					.slice(0, 5);
				setInsights(recent);
			})
			.catch((err) => logger.error('CompetitorMonitorPanel fetch failed', err))
			.finally(() => setLoading(false));
	}, [userId, api]);

	return (
		<SeededJobPanel
			panelProcess={panelProcess}
			fallbackTitle="Similar Businesses Monitor"
			isLoading={loading}
			onBack={onBack}
			onClose={onClose}
			style={style}
		>
			<div className="flex flex-col gap-6">
				<section>
					<h3
						className="mb-3 text-xs font-semibold tracking-widest
							text-(--text-disabled) uppercase"
					>
						Similar Businesses
					</h3>
					{competitors.length === 0 ? (
						<p className="text-sm text-(--text-disabled)">
							No similar businesses tracked yet.
						</p>
					) : (
						<div className="flex flex-col gap-2">
							{competitors.map((c) => (
								<div
									key={c.id}
									className="flex items-center justify-between rounded-lg border
										border-(--glass-border) px-3 py-2"
								>
									<div className="flex flex-col gap-0.5">
										<span className="text-sm font-medium text-(--text-primary)">
											{c.name}
										</span>
										{c.social_media?.instagram?.url && (
											<span className="text-xs text-(--text-disabled)">
												{c.social_media.instagram.url.replace(
													/^https?:\/\/(www\.)?/,
													'',
												)}
											</span>
										)}
									</div>
									{c.last_analyzed_at && (
										<span className="text-xs text-(--text-disabled)">
											{formatRelativeTime(c.last_analyzed_at)}
										</span>
									)}
								</div>
							))}
						</div>
					)}
				</section>

				<section>
					<h3
						className="mb-3 text-xs font-semibold tracking-widest
							text-(--text-disabled) uppercase"
					>
						Recent Insights
					</h3>
					{insights.length === 0 ? (
						<p className="text-sm text-(--text-disabled)">
							No insights yet. The monitor will produce insights on its next
							run.
						</p>
					) : (
						<div className="flex flex-col gap-2">
							{insights.map((insight) => {
								const category = insight.category as CuratedInsightCategory;
								return (
									<div
										key={insight.id}
										className="flex flex-col gap-1 rounded-lg border
											border-(--glass-border) px-3 py-2"
									>
										<div className="flex items-center gap-2">
											{CATEGORY_LABEL[category] && (
												<span
													className={cn(
														`rounded-full px-2 py-0.5 text-[0.625rem]
															font-medium`,
														CATEGORY_PILL[category] ??
															'bg-gray-100 text-gray-700',
													)}
												>
													{CATEGORY_LABEL[category]}
												</span>
											)}
											<span className="text-xs text-(--text-disabled)">
												{formatRelativeTime(insight.created_at)}
											</span>
										</div>
										<span className="text-sm text-(--text-primary)">
											{insight.title}
										</span>
										{insight.summary && (
											<span className="text-xs text-(--text-secondary)">
												{insight.summary}
											</span>
										)}
									</div>
								);
							})}
						</div>
					)}
				</section>
			</div>
		</SeededJobPanel>
	);
};
